import { Badge, Button, Card, Center, Group, Loader, Stack, Table, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/errors';
import { useRefreshRunTypes, useRunTypes } from '@/api/runs';
import { EmptyState } from '@/components/EmptyState';
import { useFormat } from '@/components/useFormat';
import { usePageTitle } from '@/components/usePageTitle';
import { visibleRunTypes } from '@/features/runs/visibleRunTypes';
import { brand } from '@/theme/theme';

export const Route = createFileRoute('/_staff/staff/run-types/')({ component: RunTypesPage });

function RunTypesPage() {
  const { t } = useTranslation('runs');
  usePageTitle(t('types.title'));
  const { relative } = useFormat();
  const types = useRunTypes();
  const refresh = useRefreshRunTypes();
  const [showInactive, setShowInactive] = useState(false);

  const doRefresh = async () => {
    try {
      const r = await refresh.mutateAsync();
      notifications.show({ color: 'forest', message: t('types.refreshed', { added: r.added.length, changed: r.changed.length, inactive: r.inactive.length }) });
    } catch (err) {
      notifications.show({ color: 'red', message: errorMessage(err) });
    }
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={1}>{t('types.title')}</Title>
        <Group gap="xs">
          <Button variant="default" onClick={() => setShowInactive((v) => !v)}>
            {showInactive ? t('types.hideInactive') : t('types.showInactive')}
          </Button>
          <Button loading={refresh.isPending} onClick={() => void doRefresh()}>
            {t('types.refresh')}
          </Button>
        </Group>
      </Group>
      {types.isPending ? (
        <Center py="xl">
          <Loader color="forest" />
        </Center>
      ) : types.isError ? (
        <EmptyState title={errorMessage(types.error)} />
      ) : types.data.length === 0 ? (
        <EmptyState title={t('types.empty')} description={t('types.emptyHint')} action={{ label: t('types.refresh'), onClick: () => void doRefresh() }} />
      ) : (
        <Card p={0}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('types.key')}</Table.Th>
                <Table.Th>{t('types.scope')}</Table.Th>
                <Table.Th>{t('types.inputs')}</Table.Th>
                <Table.Th>{t('types.outputs')}</Table.Th>
                <Table.Th>{t('types.active')}</Table.Th>
                <Table.Th>{t('types.lastSeen')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {visibleRunTypes(types.data, showInactive).map((rt) => (
                <Table.Tr key={rt.key} style={rt.is_active ? undefined : { opacity: 0.6 }}>
                  <Table.Td>
                    <Text fw={600}>{rt.key}</Text>
                  </Table.Td>
                  <Table.Td>{rt.scope}</Table.Td>
                  <Table.Td>
                    {(rt.required_inputs as Array<{ role: string; kind: string }>).map((i) => (
                      <Text key={i.role} fz={12} ff="monospace">
                        {i.role}: {i.kind}
                      </Text>
                    ))}
                    {(rt.optional_inputs as Array<{ role: string; kind: string }>).map((i) => (
                      <Text key={i.role} fz={12} ff="monospace" c={brand.textMuted}>
                        {i.role}?: {i.kind}
                      </Text>
                    ))}
                  </Table.Td>
                  <Table.Td>
                    <Text fz={12} ff="monospace">
                      {(rt.outputs as string[]).join(', ')}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={rt.is_active ? 'forest' : 'gray'} variant="light">
                      {rt.is_active ? t('types.yes') : t('types.no')}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text fz={12} c={brand.textMuted}>
                      {relative(rt.last_seen_at)}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}
    </Stack>
  );
}
